import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/DataTable";
import {
  RotateCcw,
  ShieldCheck,
  Banknote,
  CreditCard,
  Wrench,
  Check,
  X,
  AlertTriangle,
  Info as InfoIcon,
  ArrowRight,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  ExternalLink,
} from "lucide-react";

export function HelpRefundsPage() {
  return (
    <div>
      <PageHeader
        title="Guide : rembourser un paiement"
        breadcrumbs={[
          { label: "Aide", to: "/help" },
          { label: "Remboursement" },
        ]}
      />

      {/* Intro */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-danger-50 text-danger-700 flex items-center justify-center shrink-0">
              <RotateCcw size={22} />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-[#181c32] text-base mb-1">
                À quoi sert un remboursement ?
              </div>
              <p className="text-sm text-[#3f4254] leading-relaxed">
                Le remboursement (<em>refund</em>) permet de restituer au passager tout ou partie
                d'un paiement déjà encaissé. L'opération est initiée depuis le dashboard,
                puis relayée à l'opérateur (Mobile Money ou carte bancaire) qui crédite le
                compte du client. Un webhook est ensuite envoyé à votre callback URL.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Prérequis */}
      <Section
        title="Prérequis"
        subtitle="Ce qui doit être vrai avant d'initier un remboursement"
      >
        <ul className="space-y-2.5">
          <Prereq>
            Le paiement a le statut{" "}
            <Badge color="success">succeeded</Badge>. On ne peut pas rembourser un
            paiement en <em>pending</em>, <em>failed</em> ou <em>canceled</em>.
          </Prereq>
          <Prereq>
            Le montant restant à rembourser (<em>montant initial − déjà remboursé</em>)
            est strictement supérieur à zéro.
          </Prereq>
          <Prereq>
            Votre rôle vous y autorise (voir la section <strong>Qui peut rembourser ?</strong>{" "}
            ci-dessous).
          </Prereq>
          <Prereq>
            En mode test, le montant demandé respecte le plafond de sécurité (1 USD ou
            100 CDF maximum).
          </Prereq>
        </ul>
      </Section>

      {/* Qui peut rembourser */}
      <Section title="Qui peut rembourser ?" subtitle="Matrice par rôle">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f9fafb] border-b-2 border-[#e4e6ef]">
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-bold text-[#3f4254]">
                  Rôle
                </th>
                <th className="px-4 py-3 text-center text-xs uppercase tracking-wider font-bold text-[#3f4254]">
                  Initier un refund
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-bold text-[#3f4254]">
                  Pourquoi
                </th>
              </tr>
            </thead>
            <tbody>
              <RoleRow
                icon={<ShieldCheck size={16} className="text-brand-700" />}
                name="Admin marchand"
                allowed
                why="Administrateur du compte, accès complet."
              />
              <RoleRow
                icon={<Banknote size={16} className="text-info-700" />}
                name="Finance"
                allowed
                why="Comptabilité et trésorerie, responsable des flux."
              />
              <RoleRow
                icon={<CreditCard size={16} className="text-success-700" />}
                name="Commercial"
                allowed={false}
                why="Lecture seule sur les paiements — pas d'opération financière."
              />
              <RoleRow
                icon={<Wrench size={16} className="text-warning-700" />}
                name="Informatique"
                allowed={false}
                why="Debug technique uniquement — un refund est une opération financière."
              />
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-info-50 border-l-4 border-info-500 px-4 py-3">
          <InfoIcon size={16} className="text-info-700 shrink-0 mt-0.5" />
          <div className="text-xs text-info-700 leading-relaxed">
            Si vous êtes en rôle Commercial ou IT et devez rembourser un client,
            demandez à un membre Finance ou à l'Admin marchand de le faire depuis
            son compte. La trace est nominative dans l'audit log.
          </div>
        </div>
      </Section>

      {/* Procédure pas à pas */}
      <Section
        title="Procédure pas à pas"
        subtitle="De la recherche du paiement à la confirmation du refund"
      >
        <ol className="space-y-4">
          <Step n={1} title="Ouvrir la page Paiements">
            Dans le menu latéral, cliquez sur <strong>Transactions → Paiements</strong>.
            Vous pouvez filtrer par statut, méthode de paiement, période, ou rechercher
            par référence externe / numéro de billet.
          </Step>
          <Step n={2} title="Ouvrir le détail du paiement">
            Cliquez sur la ligne du paiement concerné. Vérifiez que le statut est bien{" "}
            <Badge color="success">succeeded</Badge> et que le nom du client correspond
            à la demande.
          </Step>
          <Step n={3} title="Cliquer sur « Rembourser »">
            En haut à droite de la page détail, le bouton rouge{" "}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-danger-50 text-danger-700 text-xs font-bold">
              <RotateCcw size={11} /> Rembourser
            </span>{" "}
            n'apparaît que si le paiement est éligible et que votre rôle vous y autorise.
            S'il ne s'affiche pas, vérifiez le statut ou vos droits.
          </Step>
          <Step n={4} title="Saisir le montant à rembourser">
            Le modal pré-remplit le montant maximum remboursable. Vous pouvez le laisser
            tel quel (refund total) ou le diminuer (refund partiel). Le montant est saisi
            en <strong>unité mineure</strong> : centimes pour USD/EUR, unité pour CDF —
            voir la section suivante.
          </Step>
          <Step n={5} title="Ajouter un motif (optionnel)">
            Le motif est facultatif mais fortement recommandé : il sert de trace pour
            l'audit et le support client. Exemples : <em>« annulation du vol PNR ABCD12 »</em>,{" "}
            <em>« double débit »</em>, <em>« demande client — insatisfaction »</em>.
            500 caractères maximum.
          </Step>
          <Step n={6} title="Confirmer">
            Cliquez sur <strong>Confirmer le remboursement</strong>. Le dashboard envoie
            la demande au backend, qui la relaie au switch de l'opérateur. Un message de
            succès s'affiche avec l'ID du refund.
          </Step>
          <Step n={7} title="Suivre le statut">
            Le refund apparaît immédiatement dans la liste des remboursements du paiement,
            en statut <Badge color="warning">pending</Badge> ou{" "}
            <Badge color="info">processing</Badge>. Il passe à{" "}
            <Badge color="success">succeeded</Badge> une fois confirmé par l'opérateur
            (quelques secondes à quelques minutes).
          </Step>
        </ol>
      </Section>

      {/* Unité mineure */}
      <Section
        title="Comprendre l'unité mineure"
        subtitle="Toujours saisir en centimes (USD/EUR) ou en unité (CDF)"
      >
        <p className="text-sm text-[#3f4254] leading-relaxed mb-4">
          Le champ « Montant à rembourser » attend la valeur en{" "}
          <strong>unité mineure</strong>, comme partout dans l'API. Cela évite les erreurs
          d'arrondi flottant sur les paiements financiers.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg bg-[#f5f8fa] p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold mb-2">
              USD / EUR
            </div>
            <div className="text-sm text-[#181c32]">
              1 USD = <span className="font-mono font-bold">100</span> (centimes)
              <br />
              15,50 USD = <span className="font-mono font-bold">1550</span>
              <br />
              0,99 USD = <span className="font-mono font-bold">99</span>
            </div>
          </div>
          <div className="rounded-lg bg-[#f5f8fa] p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold mb-2">
              CDF
            </div>
            <div className="text-sm text-[#181c32]">
              1 CDF = <span className="font-mono font-bold">1</span>
              <br />
              5 000 CDF = <span className="font-mono font-bold">5000</span>
              <br />
              25 000 CDF = <span className="font-mono font-bold">25000</span>
            </div>
          </div>
        </div>
      </Section>

      {/* Mode test */}
      <Section
        title="Mode test vs mode live"
        subtitle="Un plafond de sécurité protège les tests"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className="text-amber-700" />
              <div className="font-bold text-amber-800 text-sm">Mode test</div>
            </div>
            <p className="text-xs text-amber-800 leading-relaxed mb-2">
              Sur les paiements créés en mode test (<code className="font-mono">livemode: false</code>),
              le montant remboursable est <strong>plafonné à 1 USD (100 centimes) ou 100 CDF</strong>{" "}
              par refund. Ce garde-fou est appliqué côté backend et ne peut pas être contourné.
            </p>
            <p className="text-xs text-amber-800 leading-relaxed">
              Il évite tout mouvement d'argent non maîtrisé pendant les recettes et l'intégration.
            </p>
          </div>
          <div className="rounded-lg border-l-4 border-success-500 bg-success-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 size={16} className="text-success-700" />
              <div className="font-bold text-success-800 text-sm">Mode live</div>
            </div>
            <p className="text-xs text-success-800 leading-relaxed">
              En production (<code className="font-mono">livemode: true</code>), aucun plafond
              artificiel : vous pouvez rembourser jusqu'au montant restant du paiement.
              Le montant réellement débité de votre wallet correspond à ce que vous saisissez
              (les frais initiaux ne sont pas remboursés par l'opérateur).
            </p>
          </div>
        </div>
      </Section>

      {/* Statuts */}
      <Section title="Comprendre les statuts d'un refund" subtitle="Cycle de vie">
        <div className="space-y-3">
          <StatusRow
            icon={<Clock size={16} className="text-warning-700" />}
            badge={<Badge color="warning">pending</Badge>}
            title="En attente"
            text="Le refund a été créé et enregistré. La demande est en cours d'envoi au switch de l'opérateur."
          />
          <StatusRow
            icon={<Loader2 size={16} className="text-info-700" />}
            badge={<Badge color="info">processing</Badge>}
            title="En traitement"
            text="Le switch a accepté la demande et l'opérateur (Vodacom / Airtel / Orange / carte) traite le remboursement côté client."
          />
          <StatusRow
            icon={<CheckCircle2 size={16} className="text-success-700" />}
            badge={<Badge color="success">succeeded</Badge>}
            title="Réussi"
            text="Le remboursement est confirmé. Le client a été crédité, votre wallet a été débité du même montant. Un webhook a été envoyé à votre callback URL."
          />
          <StatusRow
            icon={<XCircle size={16} className="text-danger-700" />}
            badge={<Badge color="danger">failed</Badge>}
            title="Échec"
            text="L'opérateur a refusé le refund. Le champ « Motif de l'échec » sur le détail du refund donne la raison. Votre wallet n'a pas été impacté. Vous pouvez retenter."
          />
        </div>
      </Section>

      {/* Remboursements partiels */}
      <Section
        title="Remboursements partiels et multiples"
        subtitle="Vous pouvez rembourser en plusieurs fois"
      >
        <p className="text-sm text-[#3f4254] leading-relaxed mb-3">
          Un même paiement peut faire l'objet de plusieurs refunds, tant que le cumul ne
          dépasse pas le montant initial. Le dashboard affiche à chaque fois le
          <strong> montant remboursable maximum</strong> restant.
        </p>
        <div className="rounded-lg bg-[#f5f8fa] p-4 text-sm">
          <div className="font-bold text-[#181c32] mb-2">Exemple</div>
          <div className="text-[#3f4254] space-y-1">
            <div>Paiement initial : <span className="font-mono">50 000 CDF</span></div>
            <div className="flex items-center gap-2">
              <ArrowRight size={14} className="text-[#7e8299]" />
              Refund #1 : <span className="font-mono">20 000 CDF</span> — reste{" "}
              <span className="font-mono font-bold">30 000 CDF</span>
            </div>
            <div className="flex items-center gap-2">
              <ArrowRight size={14} className="text-[#7e8299]" />
              Refund #2 : <span className="font-mono">30 000 CDF</span> — reste{" "}
              <span className="font-mono font-bold">0 CDF</span> (bouton{" "}
              <em>Rembourser</em> masqué)
            </div>
          </div>
        </div>
      </Section>

      {/* Erreurs courantes */}
      <Section title="Erreurs et messages courants" subtitle="Comment les résoudre">
        <div className="space-y-3">
          <ErrorRow
            title="Le bouton « Rembourser » n'apparaît pas"
            causes={[
              "Le paiement n'est pas en statut succeeded.",
              "Le paiement est déjà entièrement remboursé.",
              "Votre rôle n'a pas la permission (Commercial ou IT).",
            ]}
            fix="Vérifiez le statut du paiement en haut de la page. Si vous devez rembourser, demandez à un membre Finance ou à l'Admin marchand."
          />
          <ErrorRow
            title="« Montant supérieur au plafond »"
            causes={[
              "Vous êtes en mode test et vous avez dépassé 1 USD (100 centimes) ou 100 CDF.",
              "Vous avez saisi un montant supérieur au restant remboursable.",
            ]}
            fix="Réduisez le montant. En mode test, restez sous 100 (unité mineure)."
          />
          <ErrorRow
            title="« Payment not found »"
            causes={[
              "Le paiement n'appartient pas à votre marchand (paiement d'un autre tenant).",
              "L'ID du paiement est erroné ou copié partiellement.",
            ]}
            fix="Retournez à la liste des paiements et rouvrez le détail depuis un lien fiable."
          />
          <ErrorRow
            title="Le refund reste en « pending » longtemps"
            causes={[
              "L'opérateur (Vodacom, Airtel, Orange, carte) tarde à confirmer.",
              "Panne temporaire du switch — un incident peut être en cours côté opérateur.",
            ]}
            fix="Attendez quelques minutes puis rechargez la page. Si le refund reste bloqué au-delà de 30 minutes, contactez support@gofreshpay.com avec l'ID du refund."
          />
          <ErrorRow
            title="Refund « failed » — pourquoi ?"
            causes={[
              "Solde du wallet marchand insuffisant pour couvrir le refund.",
              "Compte client fermé ou restreint côté opérateur.",
              "Rejet réglementaire (AML, KYC) côté opérateur.",
            ]}
            fix="Ouvrez le détail du refund : le champ « Motif de l'échec » donne le code renvoyé par le switch. Vous pouvez retenter une fois le blocage levé."
          />
        </div>
      </Section>

      {/* Callback / webhook */}
      <Section title="Webhook envoyé au marchand" subtitle="Ce que reçoit votre back-office">
        <p className="text-sm text-[#3f4254] leading-relaxed mb-3">
          Une fois le statut du refund figé (<em>succeeded</em> ou <em>failed</em>), le
          backend envoie automatiquement un webhook <code className="font-mono">refund.updated</code>{" "}
          à la callback URL configurée dans vos paramètres marchand. Assurez-vous que
          cette URL est bien joignable en HTTPS et que votre serveur répond en{" "}
          <code className="font-mono">2xx</code> sous 10 secondes.
        </p>
        <div className="text-sm text-[#3f4254] leading-relaxed">
          Vous pouvez rejouer un webhook à tout moment depuis{" "}
          <Link to="/transactions/callback-resend" className="text-brand-700 hover:underline">
            Transactions → Renvoyer callback
          </Link>{" "}
          (rôle IT ou Admin marchand).
        </div>
      </Section>

      {/* Support */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
              <ExternalLink size={20} />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-[#181c32] text-sm mb-1">
                Un cas non couvert par ce guide ?
              </div>
              <p className="text-sm text-[#3f4254] leading-relaxed">
                Contactez l'équipe Moko Afrika à{" "}
                <a
                  href="mailto:support@gofreshpay.com"
                  className="text-brand-700 hover:underline font-medium"
                >
                  support@gofreshpay.com
                </a>{" "}
                en précisant l'ID du paiement et, si applicable, l'ID du refund. Une
                trace complète est disponible dans les logs internes pour toute
                investigation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card mb-6">
      <div className="card-header">
        <div>
          <div className="card-title">{title}</div>
          {subtitle && <div className="card-subtitle">{subtitle}</div>}
        </div>
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function Prereq({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 text-sm text-[#3f4254] leading-relaxed">
      <Check size={16} className="text-success-700 shrink-0 mt-0.5" />
      <span>{children}</span>
    </li>
  );
}

function RoleRow({
  icon,
  name,
  allowed,
  why,
}: {
  icon: React.ReactNode;
  name: string;
  allowed: boolean;
  why: string;
}) {
  return (
    <tr className="border-b border-[#eff2f5] hover:bg-[#fcfcfd]">
      <td className="px-6 py-3">
        <div className="flex items-center gap-2 font-medium text-[#181c32]">
          {icon}
          {name}
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        {allowed ? (
          <Check size={18} className="inline text-success-700" />
        ) : (
          <X size={18} className="inline text-danger-500" />
        )}
      </td>
      <td className="px-4 py-3 text-sm text-[#3f4254]">{why}</td>
    </tr>
  );
}

function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-start gap-4">
      <div className="w-8 h-8 rounded-full bg-brand-700 text-white font-bold text-sm flex items-center justify-center shrink-0">
        {n}
      </div>
      <div className="min-w-0 flex-1 pt-1">
        <div className="font-bold text-[#181c32] text-sm mb-1">{title}</div>
        <p className="text-sm text-[#3f4254] leading-relaxed">{children}</p>
      </div>
    </li>
  );
}

function StatusRow({
  icon,
  badge,
  title,
  text,
}: {
  icon: React.ReactNode;
  badge: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-[#f9fafb]">
      <div className="shrink-0 mt-0.5">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <div className="font-bold text-[#181c32] text-sm">{title}</div>
          {badge}
        </div>
        <p className="text-sm text-[#3f4254] leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

function ErrorRow({
  title,
  causes,
  fix,
}: {
  title: string;
  causes: string[];
  fix: string;
}) {
  return (
    <div className="rounded-lg border border-[#eff2f5] p-4">
      <div className="flex items-start gap-2 mb-2">
        <AlertTriangle size={16} className="text-warning-700 shrink-0 mt-0.5" />
        <div className="font-bold text-[#181c32] text-sm">{title}</div>
      </div>
      <div className="pl-6">
        <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold mb-1.5">
          Causes possibles
        </div>
        <ul className="space-y-1 mb-3">
          {causes.map((c) => (
            <li key={c} className="text-sm text-[#3f4254] leading-relaxed">
              — {c}
            </li>
          ))}
        </ul>
        <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold mb-1.5">
          Que faire
        </div>
        <p className="text-sm text-[#3f4254] leading-relaxed">{fix}</p>
      </div>
    </div>
  );
}
